import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Send, Archive, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export default function TrainingPackageReviewPage() {
  const { packageId } = useParams<{ packageId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: '', summary: '', whatChanged: '', whyItChanged: '', whatToDoDifferently: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['training-package', packageId],
    queryFn: () => api.training.getPackage(packageId!),
    enabled: !!packageId,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Record<string, unknown>) => api.training.updatePackage(packageId!, updates),
    onSuccess: () => {
      toast.success('Training updated');
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['training-package', packageId] });
    },
    onError: () => toast.error('Update failed'),
  });

  const publishMutation = useMutation({
    mutationFn: () => api.training.publishPackage(packageId!),
    onSuccess: (result) => {
      toast.success(`Published — assigned to ${result.assigned} staff member${result.assigned === 1 ? '' : 's'}`);
      queryClient.invalidateQueries({ queryKey: ['training-package', packageId] });
      queryClient.invalidateQueries({ queryKey: ['training-drafts'] });
      queryClient.invalidateQueries({ queryKey: ['training-dashboard'] });
    },
    onError: () => toast.error('Failed to publish'),
  });

  const archiveMutation = useMutation({
    mutationFn: () => api.training.archivePackage(packageId!),
    onSuccess: () => {
      toast.success('Package archived');
      navigate('/training/dashboard');
    },
  });

  const pkg = data?.data?.package as Record<string, unknown> | undefined;
  const assignments = (data?.data?.assignments ?? []) as Record<string, unknown>[];

  if (isLoading || !pkg) {
    return <div className="p-6">Loading...</div>;
  }

  const isDraft = pkg.status === 'draft';
  const handleEdit = () => {
    setForm({
      title: pkg.title as string,
      summary: pkg.summary as string,
      whatChanged: pkg.whatChanged as string,
      whyItChanged: pkg.whyItChanged as string,
      whatToDoDifferently: pkg.whatToDoDifferently as string,
    });
    setEditing(true);
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/training/dashboard')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </button>

      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className={`rounded px-2 py-0.5 text-[10px] font-semibold uppercase ${
              pkg.status === 'draft' ? 'bg-yellow-500/15 text-yellow-500' :
              pkg.status === 'published' ? 'bg-compliance-low/15 text-compliance-low' :
              'bg-muted text-muted-foreground'
            }`}>
              {pkg.status as string}
            </span>
            {pkg.aiConfidence !== null && (
              <span className="text-xs text-muted-foreground">
                AI confidence: {Math.round(((pkg.aiConfidence as number) ?? 0) * 100)}%
              </span>
            )}
          </div>
          <h1 className="mt-2 text-2xl font-bold">{pkg.title as string}</h1>
        </div>
        <div className="flex gap-2">
          {isDraft && !editing && (
            <>
              <button onClick={handleEdit} className="flex items-center gap-1 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted">
                <Edit3 className="h-3.5 w-3.5" /> Edit
              </button>
              <button
                onClick={() => publishMutation.mutate()}
                disabled={publishMutation.isPending}
                className="flex items-center gap-1 rounded-md bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" /> {publishMutation.isPending ? 'Publishing...' : 'Publish & Assign'}
              </button>
            </>
          )}
          {pkg.status === 'published' && (
            <button onClick={() => archiveMutation.mutate()} className="flex items-center gap-1 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted">
              <Archive className="h-3.5 w-3.5" /> Archive
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-4 rounded-lg border border-border bg-card p-4">
          <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <Field label="Summary" value={form.summary} onChange={(v) => setForm({ ...form, summary: v })} textarea />
          <Field label="What Changed" value={form.whatChanged} onChange={(v) => setForm({ ...form, whatChanged: v })} textarea />
          <Field label="Why It Changed" value={form.whyItChanged} onChange={(v) => setForm({ ...form, whyItChanged: v })} textarea />
          <Field label="What To Do Differently" value={form.whatToDoDifferently} onChange={(v) => setForm({ ...form, whatToDoDifferently: v })} textarea />
          <div className="flex gap-2">
            <button onClick={() => updateMutation.mutate(form)} disabled={updateMutation.isPending}
              className="rounded bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50">
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
            <button onClick={() => setEditing(false)} className="rounded border border-border px-4 py-2 text-sm hover:bg-muted">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-5 rounded-lg border border-border bg-card p-6">
          <Section title="Summary" text={pkg.summary as string} />
          <Section title="What Changed" text={pkg.whatChanged as string} />
          <Section title="Why It Changed" text={pkg.whyItChanged as string} />
          <Section title="What To Do Differently" text={pkg.whatToDoDifferently as string} />
          {Boolean(pkg.regulatoryReference) && (
            <div className="text-xs text-muted-foreground">
              Regulatory reference: {pkg.regulatoryReference as string}
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            Roles affected: {((pkg.rolesAffected as string[]) ?? []).join(', ')}
          </div>
        </div>
      )}

      {assignments.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold mb-2">Assignments ({assignments.length})</h2>
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-2 text-left">Staff</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Due</th>
                  <th className="px-4 py-2 text-left">Acknowledged</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((a) => (
                  <tr key={a.id as string} className="border-t border-border">
                    <td className="px-4 py-2">{(a.userName as string) ?? (a.userEmail as string)}</td>
                    <td className="px-4 py-2 text-muted-foreground">{a.assignedRole as string}</td>
                    <td className="px-4 py-2">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                        a.status === 'acknowledged' ? 'bg-compliance-low/15 text-compliance-low' :
                        a.status === 'overdue' ? 'bg-compliance-critical/15 text-compliance-critical' :
                        'bg-muted text-muted-foreground'
                      }`}>{a.status as string}</span>
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {new Date(a.dueDate as string).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {a.acknowledgedAt ? new Date(a.acknowledgedAt as string).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, text }: { readonly title: string; readonly text: string }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{title}</h3>
      <p className="text-sm whitespace-pre-wrap">{text}</p>
    </div>
  );
}

function Field({ label, value, onChange, textarea }: {
  readonly label: string; readonly value: string; readonly onChange: (v: string) => void; readonly textarea?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{label}</label>
      {textarea ? (
        <textarea className="w-full rounded border border-border bg-background p-2 text-sm min-h-[80px]"
          value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className="w-full rounded border border-border bg-background p-2 text-sm"
          value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}
